import { Component, input, output, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { Observable, forkJoin } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Monitor } from '../../models/monitor.model';
import { MonitorsService } from '../../services/monitors.service';
import { AlarmsService } from '/app/alarms/services/alarms.service';
import { Evento } from '/app/alarms/models/evento.model';
import { ConfirmDialogComponent } from '/app/components/confirm/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HelpService } from '/app/services/help/help.service';
import { User } from '/app/users/models/users.model';
import { UsersService } from '/app/users/services/usuers.service';

interface EventColorItem {
  eventName: string;
  color: string;
  eventId: number;
}

@Component({
  selector: 'app-monitors-details',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatTabsModule,
    MatCheckboxModule,
    MatTooltipModule
  ],
  templateUrl: './monitors-details.component.html',
  styleUrls: ['./monitors-details.component.scss'],
})
export class MonitorsDetailsComponent implements OnInit {
  monitorId = input<string | null>(null);
  closed = output<void>();
  showError = output<string>();
  showSuccess = output<string>();

  monitor = signal<Monitor | null>(null);
  monitorsList = signal<Monitor[]>([]);
  monitorControl = new FormControl('');

  eventColorsArray = signal<EventColorItem[]>([]);
  displayedColumnsWithActions: string[] = ['eventName', 'color', 'actions'];
  displayedUserColumns: string[] = ['nombre', 'apellido', 'actions'];

  showAddColorForm = signal(false);
  showAddUserForm = signal(false);

  showMap = signal(false);
  showTable = signal(false);

  get showMapValue(): boolean { return this.showMap(); }
  set showMapValue(v: boolean) { this.showMap.set(v); }

  get showTableValue(): boolean { return this.showTable(); }
  set showTableValue(v: boolean) { this.showTable.set(v); }

  newColor = { idAlarma: '', color: '#000000' };
  eventos = signal<Evento[]>([]);
  filteredEventos$!: Observable<Evento[]>;
  eventoControl = new FormControl('');

  eventUsersArray = signal<string[]>([]);
  tableUsersArray = signal<User[]>([]);
  totalUsers = 0;
  pageSize = 100;

  dataSource = signal<User[]>([]);

  userControl = new FormControl('');
  filteredUsers$!: Observable<User[]>;
  selectedUser = signal<User | null>(null);

  private monitorsService = inject(MonitorsService);
  private alarmsService = inject(AlarmsService);
  private dialog = inject(MatDialog);
  private usersService = inject(UsersService);
  private helpService = inject(HelpService);

  showHelp(): void {
    this.helpService.startTour(this.helpService.getMonitorDetailsHelpSteps());
  }

  ngOnInit(): void {
    this.loadUsers(() => {
      this.loadMonitors();
      this.loadEventos();
      this.setupAutocompleteReset();

      this.filteredUsers$ = this.userControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterUsers(value || ''))
      );

      this.monitorControl.valueChanges.subscribe(monitorId => {
        if (monitorId) {
          this.onMonitorSelected(monitorId);
          this.onMonitorSelectedForUsers(monitorId);
        }
      });
    });
  }

  private setupAutocompleteReset(): void {
    this.eventoControl.valueChanges.subscribe(value => {
      if (typeof value === 'string' && value.trim() === '') {
        this.filteredEventos$ = this.createFilterObservable('');
      }
    });
  }

  private createFilterObservable(initial: string): Observable<Evento[]> {
    return this.eventoControl.valueChanges.pipe(
      startWith(initial),
      map(value => this.filterEventos(value || ''))
    );
  }

  toggleAddColorForm(): void {
    this.showAddColorForm.update(v => !v);
    if (this.showAddColorForm()) {
      this.eventoControl.setValue('');
      this.newColor = { idAlarma: '', color: '#000000' };
      this.filteredEventos$ = this.createFilterObservable('');
    }
  }

  toggleAddUserForm(): void {
    this.showAddUserForm.update(v => !v);
  }

  cancelForm(): void {
    this.showAddColorForm.set(false);
    this.newColor = { idAlarma: '', color: '' };
    this.eventoControl.setValue('');
  }

  cancelUserForm(): void {
    this.showAddUserForm.set(false);
  }

  closeDetails(): void {
    this.closed.emit();
  }

  loadMonitors(): void {
    this.monitorsService.getMonitors().subscribe({
      next: (data: any) => {
        this.monitorsList.set(data as Monitor[]);

        if (this.monitorId()) {
          const selected = this.monitorsList().find(m => m.id === this.monitorId());
          if (selected) {
            this.monitorControl.setValue(selected.id);
            this.loadMonitor(selected.id);
          }
        }

        if (!this.monitorId() && this.monitorsList().length > 0) {
          const first = this.monitorsList()[0];
          this.monitorControl.setValue(first.id);
          this.loadMonitor(first.id);
        }
      },
      error: (err: any) => console.error('Error al cargar monitores:', err)
    });
  }

  private filterUsers(value: string | User): User[] {
    const filter = typeof value === 'string'
      ? value.toLowerCase()
      : value?.name.toLowerCase() || '';

    return this.dataSource().filter(u =>
      u.name.toLowerCase().includes(filter) ||
      u.familyName.toLowerCase().includes(filter)
    );
  }

  onUserSelected(user: User): void {
    if (!user) return;
    this.selectedUser.set(user);
    this.userControl.setValue(`${user.name} ${user.familyName}`, { emitEvent: false });
  }

  onMonitorSelected(monitorId: string): void {
    this.loadMonitor(monitorId);
  }

  onMonitorSelectedForUsers(monitorId: string): void {
    this.loadMonitor(monitorId);
  }

  loadMonitor(monitorId: string): void {
    this.monitorsService.getMonitor(monitorId).subscribe({
      next: data => {
        this.monitor.set(data as Monitor);

        const m: any = this.monitor() as any;
        if (m && typeof m.view === 'string') {
          if (m.view === 'MAP') {
            this.showMap.set(true);
            this.showTable.set(false);
          } else if (m.view === 'CLASSIC') {
            this.showMap.set(false);
            this.showTable.set(true);
          } else if (m.view === 'TABS') {
            this.showMap.set(true);
            this.showTable.set(true);
          } else {
            this.showMap.set(!!m.showMap);
            this.showTable.set(!!m.showTable);
          }
        } else {
          this.showMap.set(!!m.showMap);
          this.showTable.set(!!m.showTable);
        }

        this.eventUsersArray.set([]);
        this.tableUsersArray.set([]);

        const ids = this.monitor()?.allowedUserIds;
        if (Array.isArray(ids) && ids.length > 0) {
          const userIds = ids.map(id => String(id));
          const users = userIds.map(id => this.getUser(id)).filter((u): u is User => u !== null);
          users.sort((a, b) => {
            const nameA = `${a.name} ${a.familyName}`.toLowerCase();
            const nameB = `${b.name} ${b.familyName}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });

          this.eventUsersArray.set(userIds);
          this.tableUsersArray.set(users);
        } else {
          this.eventUsersArray.set([]);
          this.tableUsersArray.set([]);
        }

        if (!this.monitor()?.eventColors || Object.keys(this.monitor()!.eventColors!).length === 0) {
          this.eventColorsArray.set([]);
          return;
        }

        const observables = Object.entries(this.monitor()!.eventColors!).map(
          ([eventIdStr, color]) => {
            const eventoid = Number(eventIdStr);
            return this.alarmsService.getEventoById(eventoid).pipe(
              map((evento: Evento | undefined) => ({
                eventId: eventoid,
                eventName: evento ? evento.desc_es : `Evento ${eventIdStr}`,
                color
              }))
            );
          }
        );

        forkJoin(observables).subscribe({
          next: arr => this.eventColorsArray.set(arr),
          error: () => this.eventColorsArray.set([])
        });
      },
      error: () => {
        this.eventColorsArray.set([]);
        this.eventUsersArray.set([]);
        this.tableUsersArray.set([]);
      }
    });
  }

  getUser(userId: string) {
    return this.dataSource().find(user => String(user.cognitoSub) === String(userId)) || null;
  }

  loadUsers(callback?: () => void): void {
    this.usersService.getUsers(0, this.pageSize).subscribe({
      next: (resp: any) => {
        this.dataSource.set(resp.content.filter((user: User) => user.active));
        if (callback) callback();
      },
      error: (error) => {
        if (error?.status == 403) {
          this.onShowError("No se tiene permisos contacte al administrador del sistema");
          this.closeDetails();
        } else {
          console.error('Error cargando usuarios', error);
        }
      }
    });
  }

  loadEventos(): void {
    this.alarmsService.getAllEventos().subscribe({
      next: eventos => {
        this.eventos.set(eventos.sort((a, b) => a.desc_es.localeCompare(b.desc_es)));
        this.filteredEventos$ = this.eventoControl.valueChanges.pipe(
          startWith(''),
          map(value => this.advancedFilterEventos(value))
        );
      },
      error: err => console.error('Error al cargar eventos:', err)
    });
  }

  private advancedFilterEventos(value: string | Evento | null): Evento[] {
    const filterValue =
      typeof value === 'string'
        ? this.normalize(value)
        : this.normalize(value?.desc_es ?? '');

    const searchWords = filterValue.split(' ').filter(w => w.length > 0);

    const filtered = this.eventos().filter(evento => {
      const normalizedName = this.normalize(evento.desc_es);
      return searchWords.every(word => normalizedName.includes(word));
    });

    return filtered.sort((a, b) => {
      const aStarts = this.normalize(a.desc_es).startsWith(filterValue) ? -1 : 1;
      const bStarts = this.normalize(b.desc_es).startsWith(filterValue) ? -1 : 1;
      return aStarts - bStarts;
    });
  }

  private normalize(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private filterEventos(value: string): Evento[] {
    const filterValue = value?.toLowerCase() || '';
    return this.eventos().filter(e => e.desc_es.toLowerCase().includes(filterValue));
  }

  onEventoSelected(evento: Evento): void {
    this.newColor.idAlarma = evento.eventoid.toString();
    this.eventoControl.setValue(evento.desc_es, { emitEvent: false });
  }

  addColor(): void {
    if (!this.newColor.idAlarma || !this.newColor.color) {
      this.onShowError('Por favor, complete todos los campos.');
      return;
    }

    const alreadyExists = this.eventColorsArray().some(
      item =>
        item.eventName ===
        (this.eventos().find(e => e.eventoid.toString() === this.newColor.idAlarma)?.desc_es ||
          this.newColor.idAlarma)
    );

    if (alreadyExists) {
      this.onShowError('El color para esta alarma ya está configurado.');
      return;
    }

    const selectedEvento = this.eventos().find(e => e.eventoid.toString() === this.newColor.idAlarma);
    const eventName = selectedEvento ? selectedEvento.desc_es : this.newColor.idAlarma;

    this.eventColorsArray.update(arr => [
      ...arr,
      {
        eventId: Number(this.newColor.idAlarma),
        eventName,
        color: this.newColor.color
      }
    ]);

    const mon = this.monitor();
    if (mon) {
      if (!mon.eventColors) mon.eventColors = {};
      mon.eventColors[this.newColor.idAlarma] = this.newColor.color;
      this.updateMonitorColors();
    }

    this.cancelForm();
  }

  addUser(): void {
    const selUser = this.selectedUser();
    if (!selUser) {
      this.onShowError("Debe seleccionar un usuario.");
      return;
    }

    const userId = String(selUser.cognitoSub).trim();

    if (this.eventUsersArray().includes(userId)) {
      this.onShowError("El usuario ya está añadido a este monitor.");
      return;
    }

    const mon = this.monitor();
    if (!mon) {
      this.onShowError("Monitor no cargado.");
      return;
    }

    if (!Array.isArray(mon.allowedUserIds)) {
      mon.allowedUserIds = [];
    }

    this.eventUsersArray.update(arr => [...arr, userId]);
    this.tableUsersArray.update(arr => [...arr, selUser]);

    mon.allowedUserIds.push(userId);

    this.monitorsService.updateMonitor(mon).subscribe({
      next: () => {
        this.onShowSuccess("Usuario agregado correctamente.");

        this.selectedUser.set(null);
        this.userControl.setValue('');
        this.cancelUserForm();

        if (mon.id) {
          this.loadMonitor(mon.id);
        }
      },
      error: () => {
        this.onShowError("Error al agregar el usuario.");
      }
    });
  }

  deleteColor(index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Configuración Alarma',
        message: '¿Estás seguro de eliminar el color de la Alarma?',
        confirmText: 'Sí, Eliminar',
        cancelText: 'Cancelar',
        icon: 'exit'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const item = this.eventColorsArray()[index];
        const mon = this.monitor();
        if (mon) {
          delete mon.eventColors[item.eventId.toString()];
          this.updateMonitorColors();
        }
        this.eventColorsArray.update(arr => arr.filter((_, i) => i !== index));
      }
    });
  }

  deleteUser(index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Usuario del Monitor',
        message: '¿Estás seguro de eliminar el usuario del Monitor?',
        confirmText: 'Sí, Eliminar',
        cancelText: 'Cancelar',
        icon: 'exit'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      const mon = this.monitor();
      if (!mon || !Array.isArray(mon.allowedUserIds)) {
        this.onShowError("Monitor no cargado.");
        return;
      }

      const userToDelete = this.tableUsersArray()[index];

      if (!userToDelete) {
        this.onShowError("Usuario no encontrado.");
        return;
      }

      const userIdToDelete = String(userToDelete.cognitoSub);

      mon.allowedUserIds = mon.allowedUserIds.filter(
        id => String(id) !== userIdToDelete
      );

      this.tableUsersArray.update(arr => arr.filter(u => String(u.cognitoSub) !== userIdToDelete));
      this.eventUsersArray.update(arr => arr.filter(id => String(id) !== userIdToDelete));

      this.monitorsService.updateMonitor(mon).subscribe({
        next: () => {
          this.onShowSuccess("Usuario eliminado correctamente.");
          if (mon.id) {
            this.loadMonitor(mon.id);
          }
        },
        error: () => {
          this.onShowError("Error al eliminar el usuario.");
        }
      });
    });
  }

  saveViews(): void {
    if (!this.showMap() && !this.showTable()) {
      this.onShowError('Debe seleccionar al menos Mapa o Tabla.');
      return;
    }

    const mon = this.monitor();
    if (!mon) {
      this.onShowError('Monitor no cargado.');
      return;
    }

    const m: any = mon as any;
    m.showMap = this.showMap();
    m.showTable = this.showTable();

    if (this.showMap() && !this.showTable()) {
      m.view = 'MAP';
    } else if (!this.showMap() && this.showTable()) {
      m.view = 'CLASSIC';
    } else if (this.showMap() && this.showTable()) {
      m.view = 'TABS';
    }

    this.monitorsService.updateMonitor(mon).subscribe({
      next: () => {
        this.onShowSuccess('Vistas guardadas correctamente.');
        try {
          this.monitorsService.monitorUpdated.next(mon);
        } catch (e) {
          // ignore if subject not available
        }
      },
      error: () => this.onShowError('Error al guardar las vistas.')
    });
  }

  resetViews(): void {
    const mon = this.monitor();
    if (mon) {
      const m: any = mon as any;
      this.showMap.set(!!m.showMap);
      this.showTable.set(!!m.showTable);
    } else {
      this.showMap.set(false);
      this.showTable.set(false);
    }
  }

  updateMonitorColors(): void {
    const mon = this.monitor();
    if (!mon) return;

    this.monitorsService.updateMonitor(mon).subscribe({
      next: () => {
        this.onShowSuccess('Monitor actualizado exitosamente.');
      },
      error: () => {
        this.onShowError('Error al actualizar el monitor.');
      }
    });
  }

  onShowError(message: string): void {
    this.showError.emit(message);
  }

  onShowSuccess(message: string): void {
    this.showSuccess.emit(message);
  }
}
